import { LOAD_BAND_KEYS, MONTH_COUNT, SEGMENT_KEYS } from '../domain/constants';
import type { Case, LoadBandKey, SegmentKey, Staff } from '../domain/types';
import { sum } from '../lib/safeMath';
import { capOf, caseCountOf, deployStaff, loadOf, spareAt, usedAt } from './capacity';
import type { SenninViolation } from './caseMetrics';
import { fteOf, needAt, senninViolations, shortOf } from './caseMetrics';

export const SEGMENT_SNAPSHOT_MONTHS = [0, 6, 11] as const;
/** 看板の「3ヶ月後」(2027年1月)。 */
const NEED_LOOKAHEAD_MONTH = 3;

export const loadBandOf = (load: number): LoadBandKey => {
  if (load === 0) return 'free';
  if (load <= 50) return 'light';
  if (load <= 100) return 'fit';
  if (load <= 120) return 'over';
  return 'heavy';
};

export type SegmentRow = {
  segment: SegmentKey;
  cells: readonly { month: number; assigned: number; required: number }[];
};

export type DashboardModel = {
  staffCount: number;
  deployCount: number;
  /** 施工力(人工)= 配置対象人数 + 分業で創出した余力 */
  capacity: number;
  used: number;
  spare: number;
  /** 50% 以上まとまって空いている人の余力(人工) */
  lumpSpare: number;
  /** 稼働中の人の平均担当案件数。稼働中の人がいなければ null。 */
  avgCasesPerPerson: number | null;
  need3: number;
  gap: number;
  overCount: number;
  freeFullCount: number;
  shortCases: readonly { caseItem: Case; shortage: number }[];
  violations: readonly SenninViolation[];
  /** 担当案件数が 1 / 2 / 3 / 4以上 の人数 */
  caseCountDistribution: readonly number[];
  loadBands: readonly { key: LoadBandKey; count: number }[];
  segmentRows: readonly SegmentRow[];
  monthly: readonly { need: number; used: number }[];
};

type Input = {
  cases: readonly Case[];
  staff: readonly Staff[];
  reflectedTotal: number;
};

export const buildDashboardModel = ({ cases, staff, reflectedTotal }: Input): DashboardModel => {
  const deploy = deployStaff(staff);
  const caseIds: ReadonlySet<string> = new Set(cases.map((c) => c.id));
  const capacity = deploy.length + reflectedTotal;
  const need3 = needAt(cases, NEED_LOOKAHEAD_MONTH);
  const active = deploy.filter((s) => loadOf(s, 0) > 0);

  return {
    staffCount: staff.length,
    deployCount: deploy.length,
    capacity,
    used: usedAt(deploy, 0),
    spare: spareAt(deploy, 0),
    lumpSpare: sum(deploy.map((s) => (capOf(s, 0) >= 50 ? capOf(s, 0) : 0))) / 100,
    avgCasesPerPerson:
      active.length === 0
        ? null
        : sum(active.map((s) => caseCountOf(s, 0, caseIds))) / active.length,
    need3,
    gap: capacity - need3,
    overCount: deploy.filter((s) => loadOf(s, 0) > 100).length,
    freeFullCount: deploy.filter((s) => loadOf(s, 0) === 0).length,
    shortCases: cases
      .map((caseItem) => ({ caseItem, shortage: shortOf(staff, caseItem) }))
      .filter((x) => x.shortage > 0)
      .sort((x, y) => y.shortage - x.shortage),
    violations: senninViolations(cases, staff, 0),
    caseCountDistribution: [1, 2, 3, 4].map(
      (k) =>
        deploy.filter((s) => {
          const n = caseCountOf(s, 0, caseIds);
          return k < 4 ? n === k : n >= 4;
        }).length,
    ),
    loadBands: LOAD_BAND_KEYS.map((key) => ({
      key,
      count: deploy.filter((s) => loadBandOf(loadOf(s, 0)) === key).length,
    })),
    segmentRows: SEGMENT_KEYS.map((segment) => {
      const inSegment = cases.filter((c) => c.segment === segment);
      return {
        segment,
        cells: SEGMENT_SNAPSHOT_MONTHS.map((month) => ({
          month,
          assigned: sum(inSegment.map((c) => fteOf(staff, c, month))),
          required: needAt(inSegment, month),
        })),
      };
    }),
    monthly: Array.from({ length: MONTH_COUNT }, (_, month) => ({
      need: needAt(cases, month),
      used: usedAt(deploy, month),
    })),
  };
};
